import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Firebase initialisation
// ---------------------------------------------------------------------------
// Values are loaded from .env (REACT_APP_ prefix required by Create React App).

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Avoid re-initialising on hot reload
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
export const firestore = getFirestore(app);

// ---------------------------------------------------------------------------
// Clash Connections game config — stored in Firestore at clashconnections/game
// ---------------------------------------------------------------------------

/**
 * Puzzle structure matching the format used in the game
 */
export interface PuzzleData {
  category: string;
  words: string[];
  difficulty: number;
}

/**
 * Reads the current puzzle data from Firestore.
 * Falls back to localStorage cache, then to a safe default if Firestore fails.
 * Returns an object with { puzzle, isLocked }
 */
export const fetchPuzzleData = async () => {
  try {
    const docRef = doc(firestore, 'clashconnections', 'game');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const puzzle = data.puzzle;
      const isLocked = data.isLocked ?? false;

      // Validate the puzzle structure
      if (
        Array.isArray(puzzle) &&
        puzzle.length === 4 &&
        puzzle.every(
          cat =>
            cat.category &&
            Array.isArray(cat.words) &&
            cat.words.length === 4 &&
            cat.difficulty
        )
      ) {
        // Cache locally for offline fallback
        localStorage.setItem('clashconnections_currentPuzzle', JSON.stringify(puzzle));
        localStorage.setItem('clashconnections_isLocked', JSON.stringify(isLocked));
        return { puzzle, isLocked };
      }
    }

    // Try localStorage cache as fallback
    const cached = localStorage.getItem('clashconnections_currentPuzzle');
    const cachedLocked = localStorage.getItem('clashconnections_isLocked');
    if (cached) {
      console.log('Using cached puzzle from localStorage');
      return {
        puzzle: JSON.parse(cached),
        isLocked: cachedLocked ? JSON.parse(cachedLocked) : false
      };
    }

    // Ultimate fallback - return null to use default from the app
    return null;
  } catch (error) {
    console.error('Firestore fetch error:', error);
    // Try localStorage cache as fallback
    const cached = localStorage.getItem('clashconnections_currentPuzzle');
    const cachedLocked = localStorage.getItem('clashconnections_isLocked');
    if (cached) {
      console.log('Using cached puzzle from localStorage');
      return {
        puzzle: JSON.parse(cached),
        isLocked: cachedLocked ? JSON.parse(cachedLocked) : false
      };
    }
    // Return null to use default puzzle
    return null;
  }
};

/**
 * Writes an updated puzzle to Firestore.
 * Also caches to localStorage for offline fallback.
 * Only called from the Admin panel.
 */
export const updatePuzzleData = async (puzzle, isLocked) => {
  try {
    const docRef = doc(firestore, 'clashconnections', 'game');
    const dataToSave = { puzzle };
    if (isLocked !== undefined) {
      dataToSave.isLocked = isLocked;
    }
    await setDoc(docRef, dataToSave, { merge: true });
    // Cache locally on successful write
    localStorage.setItem('clashconnections_currentPuzzle', JSON.stringify(puzzle));
    if (isLocked !== undefined) {
      localStorage.setItem('clashconnections_isLocked', JSON.stringify(isLocked));
    }
  } catch (error) {
    console.error('Firestore update error:', error);
    throw new Error('Failed to update puzzle configuration. Please check your connection.');
  }
};

// ---------------------------------------------------------------------------
// Admin password — stored in Firestore at admin_settings/auth
// (shared with the main website and Clashdle; read-only from the frontend)
// ---------------------------------------------------------------------------

/**
 * Fetches the admin password from Firestore.
 * Returns null if the document doesn't exist or the fetch fails.
 */
export const fetchAdminPassword = async () => {
  try {
    const docRef = doc(firestore, 'admin_settings', 'auth');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.password || null;
    }
    return null;
  } catch (e) {
    console.error('Failed to fetch admin password from Firestore:', e);
    return null;
  }
};
